import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface InspecaoAtrasada {
  agenda_id: string;
  scheduled_date: string;
  agenda_status: string;
  work_id: string;
  work_name: string;
  work_address: string | null;
  hub_id: string;
  hub_name: string;
  professional_id: string;
  professional_name: string;
  dias_atraso: number;
}

export interface RelatorioCritico {
  relatorio_id: string;
  hub_id: string;
  hub_name: string;
  mes_referencia: string;
  status: string;
  criado_em: string;
  data_validacao: string | null;
  total_indicadores: number;
  prazo_vencido: boolean;
  sem_indicadores: boolean;
  dias_em_rascunho: number;
}

export interface AlertasDashboard {
  total_inspecoes_atrasadas: number;
  total_relatorios_criticos: number;
  total_relatorios_sem_indicadores: number;
  total_relatorios_prazo_vencido: number;
}

export const useAlerts = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const obterAlertasDashboard = useCallback(async (): Promise<AlertasDashboard | null> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('obter_alertas_dashboard');
      
      if (error) {
        console.error('Erro ao obter alertas:', error);
        toast({
          title: 'Erro ao carregar alertas',
          description: error.message,
          variant: 'destructive',
        });
        return null;
      }
      
      return data?.[0] || null;
    } catch (err) {
      console.error('Erro:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const listarInspecoesAtrasadas = useCallback(async (): Promise<InspecaoAtrasada[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vw_inspecoes_atrasadas')
        .select('*')
        .order('scheduled_date', { ascending: true });
      
      if (error) {
        console.error('Erro ao listar inspeções atrasadas:', error);
        toast({
          title: 'Erro ao carregar inspeções atrasadas',
          description: error.message,
          variant: 'destructive',
        });
        return [];
      }
      
      return data || [];
    } catch (err) {
      console.error('Erro:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const listarRelatoriosCriticos = useCallback(async (): Promise<RelatorioCritico[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vw_relatorios_criticos')
        .select('*')
        .order('mes_referencia', { ascending: false });
      
      if (error) {
        console.error('Erro ao listar relatórios críticos:', error);
        toast({
          title: 'Erro ao carregar relatórios críticos',
          description: error.message,
          variant: 'destructive',
        });
        return [];
      }
      
      return data || [];
    } catch (err) {
      console.error('Erro:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    obterAlertasDashboard,
    listarInspecoesAtrasadas,
    listarRelatoriosCriticos,
  };
};
